import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FlikLogo from '../../components/Logo/FlikLogo';
import FlikSelect from '../../components/Select/FlikSelect';
import { isAdmin as isAdminUser } from '../../auth/roles';
import {
  getMyProfile,
  getAdminUsers,
  getAdminUsersCount,
  getAdminPremiumCount,
  getAdminRegistrations,
  getAdminActivityTypes,
  getAdminActivityType,
  createAdminActivityType,
  replaceAdminActivityTypeFields,
  getActivityTypes,
  searchActivities,
  createActivity,
  updateActivity,
  deleteActivity,
  logout,
  ApiError,
} from '../../api/client';
import ActivityTypeIcon from '../../activities/ActivityTypeIcon';
import { resolveActivityType } from '../../activities/activityTypes';
import './AdminPage.css';

/**
 * Admin dashboard (route "/admin"). Reached from the "Admin" button on the
 * profile page, which only appears for users whose role is ADMIN. The page also
 * guards itself: it loads the signed-in profile and bounces non-admins back to
 * "/perfil", so typing the URL directly is not enough.
 *
 * Two sections:
 *  - "Resumen general": headline stat cards (total users, premium users) and a
 *    monthly registrations bar chart. Every figure without a wired endpoint is
 *    intentionally shown as "—".
 *  - "Usuarios": a searchable, role-filterable, paginated user table backed by
 *    GET /admin/users.
 *
 * Built for desktop (left rail) and mobile (top segmented control).
 */

const MONTHS_SHORT = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const ROLE_OPTIONS = [
  { value: '', label: 'Todos los roles' },
  { value: 'USER', label: 'Usuario' },
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'ADMIN', label: 'Administrador' },
];

const PAGE_SIZE = 10;
const EMPTY = '—';

/* ------------------------------ helpers -------------------------------- */

/** Groups thousands with a dot (es-ES style), independent of the ICU build. */
const nf = {
  format: (n) =>
    typeof n === 'number' && Number.isFinite(n)
      ? String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      : String(n),
};

/** Reads a numeric count from the many shapes a backend might answer with. */
function readCount(payload) {
  if (payload === null || payload === undefined) return null;
  if (typeof payload === 'number') return payload;
  if (typeof payload === 'string' && payload.trim() !== '' && !Number.isNaN(Number(payload))) {
    return Number(payload);
  }
  if (typeof payload === 'object') {
    const v = payload.count ?? payload.total ?? payload.value ?? payload.totalElements;
    return typeof v === 'number' ? v : null;
  }
  return null;
}

/** A count formatted for display, or the em-dash placeholder. */
const fmtCount = (v) => (v === null || v === undefined ? EMPTY : nf.format(v));

/**
 * Normalises the registrations payload into a fixed 12-slot array of counts
 * (index 0 = January). Accepts an array of { month, count|total|value }, a plain
 * map { "1": 10, ... }, or an array of raw numbers.
 */
function normalizeRegistrations(payload) {
  const slots = new Array(12).fill(0);
  if (!payload) return slots;

  if (Array.isArray(payload)) {
    payload.forEach((item, i) => {
      if (typeof item === 'number') {
        if (i < 12) slots[i] = item;
        return;
      }
      if (item && typeof item === 'object') {
        const month = Number(item.month ?? item.key ?? item.label);
        const count = Number(item.count ?? item.total ?? item.value ?? item.registrations ?? 0);
        if (month >= 1 && month <= 12) slots[month - 1] = count || 0;
      }
    });
    return slots;
  }

  if (typeof payload === 'object') {
    Object.entries(payload).forEach(([k, v]) => {
      const month = Number(k);
      if (month >= 1 && month <= 12) slots[month - 1] = Number(v) || 0;
    });
  }
  return slots;
}

function fullNameOf(u) {
  return [u.name, u.surname].filter(Boolean).join(' ').trim();
}

function initialsOf(u) {
  const full = fullNameOf(u);
  if (full) {
    const parts = full.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  }
  return (u.username?.[0] || u.email?.[0] || '?').toUpperCase();
}

function roleLabel(role) {
  const norm = typeof role === 'string' ? role.replace(/^ROLE_/i, '').toUpperCase() : '';
  const found = ROLE_OPTIONS.find((r) => r.value === norm);
  return found ? found.label : (role || EMPTY);
}

function roleClass(role) {
  const norm = typeof role === 'string' ? role.replace(/^ROLE_/i, '').toUpperCase() : '';
  if (norm === 'ADMIN') return 'AdminBadge AdminBadge--admin';
  if (norm === 'PREMIUM') return 'AdminBadge AdminBadge--premium';
  return 'AdminBadge';
}

const NAV = [
  { id: 'overview', label: 'Resumen general', icon: GridIcon },
  { id: 'users', label: 'Usuarios', icon: UsersIcon },
  { id: 'activityTypes', label: 'Tipos de actividad', icon: LayersIcon },
  { id: 'routes', label: 'Rutas', icon: RouteIcon },
];

const SOON = ['Moderación', 'Ajustes'];

/**
 * The FieldType enum values the admin can pick from. These MUST match the
 * backend `FieldType` enum exactly, because they are sent verbatim on save and
 * read back verbatim into the activity form. Backend values: STRING, INTEGER,
 * DOUBLE, BOOLEAN, DATE, ENUM. Lists are ENUM (single choice, with an `options`
 * array); the backend has no multi-choice type.
 */
const FIELD_TYPE_OPTIONS = [
  { value: 'STRING', label: 'Texto' },
  { value: 'INTEGER', label: 'Número entero' },
  { value: 'DOUBLE', label: 'Número decimal' },
  { value: 'BOOLEAN', label: 'Sí / No' },
  { value: 'DATE', label: 'Fecha' },
  { value: 'ENUM', label: 'Lista (una opción)' },
];

/* ------------------------------ component ------------------------------ */

function AdminPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null); // null = checking
  const [tab, setTab] = useState('overview');

  // Gate the page on the real role from GET /users/me.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await getMyProfile();
        if (!alive) return;
        if (isAdminUser(me)) {
          setAuthorized(true);
        } else {
          setAuthorized(false);
          navigate('/perfil', { replace: true });
        }
      } catch {
        if (!alive) return;
        setAuthorized(false);
        navigate('/perfil', { replace: true });
      }
    })();
    return () => {
      alive = false;
    };
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/authenticate?tab=login', { replace: true });
  };

  if (authorized !== true) {
    return (
      <div className="Admin Admin--loading">
        <FlikLogo height={22} />
        <p>Comprobando permisos…</p>
      </div>
    );
  }

  return (
    <div className="Admin">
      {/* ---- Desktop left rail ---- */}
      <aside className="Admin__sidebar">
        <button
          type="button"
          className="Admin__logoBtn"
          onClick={() => navigate('/')}
          aria-label="Ir al inicio"
        >
          <FlikLogo height={22} />
        </button>
        <span className="Admin__railTag">Panel de administración</span>

        <nav className="Admin__nav" aria-label="Secciones de administración">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`Admin__navItem${tab === id ? ' is-active' : ''}`}
              aria-current={tab === id ? 'page' : undefined}
              onClick={() => setTab(id)}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
          {SOON.map((label) => (
            <span key={label} className="Admin__navItem Admin__navItem--soon" aria-disabled="true">
              <DotIcon />
              <span>{label}</span>
              <em>Próximamente</em>
            </span>
          ))}
        </nav>

        <div className="Admin__railFoot">
          <button type="button" className="Admin__railLink" onClick={() => navigate('/perfil')}>
            ← Volver al perfil
          </button>
          <button type="button" className="Admin__railLink Admin__railLink--muted" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* ---- Body ---- */}
      <div className="Admin__body">
        {/* Mobile header + segmented tabs */}
        <header className="Admin__mobileBar">
          <button
            type="button"
            className="Admin__logoBtn"
            onClick={() => navigate('/perfil')}
            aria-label="Volver al perfil"
          >
            <FlikLogo height={18} />
          </button>
          <button type="button" className="Admin__mobileLogout" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </header>

        <div className="Admin__tabs" role="tablist" aria-label="Secciones">
          {NAV.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`Admin__tab${tab === id ? ' is-active' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        <main className="Admin__main">
          {tab === 'overview' && <OverviewSection />}
          {tab === 'users' && <UsersSection />}
          {tab === 'activityTypes' && <ActivityTypesSection />}
          {tab === 'routes' && <RoutesSection />}
        </main>
      </div>
    </div>
  );
}

/* ------------------------------ overview ------------------------------- */

function OverviewSection() {
  const year = new Date().getFullYear();
  const [total, setTotal] = useState(null);
  const [premium, setPremium] = useState(null);
  const [reg, setReg] = useState(null); // 12-slot array or null while loading
  const [regError, setRegError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const months = Array.from({ length: 12 }, (_, i) => i + 1);
      const [totalRes, premiumRes, regRes] = await Promise.allSettled([
        getAdminUsersCount(),
        getAdminPremiumCount(),
        getAdminRegistrations({ year, months }),
      ]);
      if (!alive) return;
      setTotal(totalRes.status === 'fulfilled' ? readCount(totalRes.value) : null);
      setPremium(premiumRes.status === 'fulfilled' ? readCount(premiumRes.value) : null);
      if (regRes.status === 'fulfilled') {
        setReg(normalizeRegistrations(regRes.value));
      } else {
        setRegError(true);
        setReg(new Array(12).fill(0));
      }
    })();
    return () => {
      alive = false;
    };
  }, [year]);

  const premiumShare =
    total && premium != null && total > 0 ? Math.round((premium / total) * 100) : null;

  return (
    <section className="Admin__section" aria-label="Resumen general">
      <div className="Admin__sectionHead">
        <div>
          <h1 className="Admin__title">Resumen general</h1>
          <p className="Admin__subtitle">Vista rápida del estado de la plataforma.</p>
        </div>
        <span className="Admin__pill">{year}</span>
      </div>

      <div className="Admin__stats">
        <StatCard
          label="Usuarios registrados"
          value={fmtCount(total)}
          hint="Total en la plataforma"
          tone="ink"
        />
        <StatCard
          label="Usuarios premium"
          value={fmtCount(premium)}
          hint={premiumShare != null ? `${premiumShare}% del total` : 'Suscripciones activas'}
          tone="gold"
        />
        <StatCard label="Ingresos" value={EMPTY} hint="Sin datos" tone="dark" />
        <StatCard label="Reportes activos" value={EMPTY} hint="Sin datos" tone="ink" />
      </div>

      <div className="Admin__overviewGrid">
        <RegistrationsCard year={year} data={reg} error={regError} />

        <div className="AdminCard AdminCard--muted">
          <h2 className="AdminCard__eyebrow">Lista de moderación</h2>
          <div className="Admin__empty">
            <span className="Admin__emptyMark">{EMPTY}</span>
            <p>Sin datos disponibles</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, hint, tone }) {
  return (
    <div className={`StatCard StatCard--${tone}`}>
      <span className="StatCard__label">{label}</span>
      <strong className="StatCard__value">{value}</strong>
      <span className="StatCard__hint">{hint}</span>
    </div>
  );
}

function RegistrationsCard({ year, data, error }) {
  const loading = data === null;
  const slots = data || new Array(12).fill(0);
  const max = Math.max(1, ...slots);
  const totalYear = slots.reduce((a, b) => a + b, 0);
  const peakIdx = slots.indexOf(Math.max(...slots));

  return (
    <div className="AdminCard">
      <div className="AdminCard__head">
        <div>
          <h2 className="AdminCard__eyebrow">Altas / registros</h2>
          <p className="AdminCard__lead">
            {loading
              ? 'Cargando…'
              : error
              ? 'Sin datos disponibles'
              : `${nf.format(totalYear)} registros en ${year}`}
          </p>
        </div>
      </div>

      <div className="Chart" role="img" aria-label={`Registros mensuales de ${year}`}>
        {slots.map((count, i) => {
          const pct = loading ? 0 : Math.round((count / max) * 100);
          const isPeak = !loading && !error && totalYear > 0 && i === peakIdx;
          return (
            <div className="Chart__col" key={MONTHS_SHORT[i]}>
              <div className="Chart__barWrap">
                <span className="Chart__count">{loading || error ? '' : count || ''}</span>
                <span
                  className={`Chart__bar${isPeak ? ' is-peak' : ''}`}
                  style={{ height: `${Math.max(pct, count > 0 ? 6 : 2)}%` }}
                />
              </div>
              <span className="Chart__label">{MONTHS_SHORT[i]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------- users --------------------------------- */

function UsersSection() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [page, setPage] = useState(0);

  const [data, setData] = useState(null); // Page payload
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Debounce the free-text search.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await getAdminUsers({ search, role, page, size: PAGE_SIZE });
        if (!alive) return;
        setData(res || {});
      } catch (err) {
        if (!alive) return;
        setError('No hemos podido cargar los usuarios.');
        setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [search, role, page]);

  const rows = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.content || data.items || data.users || [];
  }, [data]);

  const totalPages = data?.totalPages ?? (rows.length < PAGE_SIZE && page === 0 ? 1 : undefined);
  const totalElements = data?.totalElements;
  const currentPage = data?.number ?? page;
  const hasPrev = currentPage > 0;
  const hasNext =
    totalPages != null ? currentPage + 1 < totalPages : rows.length === PAGE_SIZE;

  const onRoleChange = (value) => {
    setRole(value);
    setPage(0);
  };

  return (
    <section className="Admin__section" aria-label="Usuarios">
      <div className="Admin__sectionHead">
        <div>
          <h1 className="Admin__title">Usuarios</h1>
          <p className="Admin__subtitle">
            {totalElements != null
              ? `${nf.format(totalElements)} usuarios registrados`
              : 'Busca y filtra los usuarios de la plataforma.'}
          </p>
        </div>
      </div>

      <div className="Admin__toolbar">
        <div className="Admin__searchBox">
          <SearchIcon />
          <input
            type="search"
            className="Admin__search"
            placeholder="Buscar por usuario, nombre o email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Buscar usuarios"
          />
        </div>
        <FlikSelect
          variant="box"
          value={role}
          onChange={onRoleChange}
          options={ROLE_OPTIONS}
          ariaLabel="Filtrar por rol"
        />
      </div>

      <div className="AdminCard AdminCard--table">
        <div className="AdminTable" role="table">
          <div className="AdminTable__head" role="row">
            <span role="columnheader">Usuario</span>
            <span role="columnheader" className="AdminTable__colEmail">Email</span>
            <span role="columnheader" className="AdminTable__colRole">Rol</span>
            <span role="columnheader" className="AdminTable__colDate">Alta</span>
          </div>

          {loading ? (
            <div className="AdminTable__state">Cargando usuarios…</div>
          ) : error ? (
            <div className="AdminTable__state AdminTable__state--error">{error}</div>
          ) : rows.length === 0 ? (
            <div className="AdminTable__state">No se han encontrado usuarios.</div>
          ) : (
            rows.map((u, i) => (
              <div className="AdminTable__row" role="row" key={u.id ?? u.username ?? u.email ?? i}>
                <div className="AdminTable__user" role="cell">
                  <span className="AdminTable__avatar" aria-hidden="true">{initialsOf(u)}</span>
                  <span className="AdminTable__userText">
                    <strong>{fullNameOf(u) || u.username || '—'}</strong>
                    <em>@{u.username || '—'}</em>
                  </span>
                </div>
                <span className="AdminTable__colEmail AdminTable__email" role="cell">
                  {u.email || EMPTY}
                </span>
                <span className="AdminTable__colRole" role="cell">
                  <span className={roleClass(u.role || (Array.isArray(u.roles) ? u.roles[0] : u.userType))}>
                    {roleLabel(u.role || (Array.isArray(u.roles) ? u.roles[0] : u.userType))}
                  </span>
                </span>
                <span className="AdminTable__colDate AdminTable__date" role="cell">
                  {formatDate(u.createdAt || u.registeredAt || u.joinedAt)}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="AdminTable__foot">
          <span className="AdminTable__pageInfo">
            {totalPages != null
              ? `Página ${currentPage + 1} de ${Math.max(totalPages, 1)}`
              : `Página ${currentPage + 1}`}
          </span>
          <div className="AdminTable__pager">
            <button
              type="button"
              className="AdminPager__btn"
              disabled={!hasPrev || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Anterior
            </button>
            <button
              type="button"
              className="AdminPager__btn"
              disabled={!hasNext || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function formatDate(iso) {
  if (!iso) return EMPTY;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* --------------------------- activity types ---------------------------- */

let fieldSeq = 0;
const nextFieldUid = () => `f${Date.now().toString(36)}${(fieldSeq += 1)}`;

/**
 * Turns a label into a valid FieldDefinition `key`: camelCase, starting with a
 * lowercase letter, only [a-zA-Z0-9_] — matching the backend's
 * ^[a-z][a-zA-Z0-9_]*$ pattern.
 */
function slugifyKey(label) {
  const words = String(label)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-zA-Z0-9\s_]/g, ' ')
    .trim()
    .split(/[\s_]+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  const camel = words
    .map((w, i) => (i === 0 ? w.toLowerCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join('');
  const cleaned = camel.replace(/^[^a-z]+/, ''); // force a lowercase-letter start
  return cleaned;
}

const KEY_PATTERN = /^[a-z][a-zA-Z0-9_]*$/;

/** Normalises one FieldDefinitionResponse into the editor's shape. */
function normalizeField(f) {
  if (!f || typeof f !== 'object') return null;
  const type = String(f.type || 'STRING').toUpperCase();
  return {
    uid: nextFieldUid(),
    key: f.key ?? '',
    label: f.label ?? f.key ?? '',
    type: FIELD_TYPE_OPTIONS.some((o) => o.value === type) ? type : 'STRING',
    placeholder: f.placeholder ?? '',
    required: Boolean(f.required),
    editableByAdmin: Boolean(f.editableByAdmin),
    // Choices for ENUM fields, held as a flat list of strings while editing.
    // Accepts either strings or `{ value, label }` objects on the wire.
    options: Array.isArray(f.options)
      ? f.options
          .map((o) =>
            o && typeof o === 'object' ? String(o.value ?? o.label ?? '') : String(o)
          )
          .filter(Boolean)
      : [],
  };
}

/** The activity type's default (non-editable) fields, read-only in the UI. */
function readDefaultFields(at) {
  if (!at || !Array.isArray(at.fields)) return [];
  return at.fields
    .filter((f) => f && !f.editableByAdmin)
    .map(normalizeField)
    .filter(Boolean);
}

/** The admin-editable custom fields. */
function readCustomFields(at) {
  if (!at || !Array.isArray(at.fields)) return [];
  return at.fields
    .filter((f) => f && f.editableByAdmin)
    .map(normalizeField)
    .filter(Boolean);
}

function activityTypeName(at) {
  return at?.name || at?.label || at?.title || `Tipo #${at?.id ?? '—'}`;
}

function ActivityTypesSection() {
  const [types, setTypes] = useState(null); // array or null while loading
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = React.useCallback(async () => {
    setError('');
    try {
      const res = await getAdminActivityTypes();
      const list = Array.isArray(res) ? res : res?.content || res?.items || [];
      setTypes(list);
    } catch (err) {
      setError('No hemos podido cargar los tipos de actividad.');
      setTypes([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = () => {
    setSelectedId(null);
    setCreating(false);
    setTypes(null);
    load();
  };

  return (
    <section className="Admin__section" aria-label="Tipos de actividad">
      <div className="Admin__sectionHead">
        <div>
          <h1 className="Admin__title">Tipos de actividad</h1>
          <p className="Admin__subtitle">
            {types
              ? `${types.length} ${types.length === 1 ? 'tipo definido' : 'tipos definidos'}`
              : 'Define los tipos de actividad y sus campos personalizados.'}
          </p>
        </div>
        <button type="button" className="Admin__cta" onClick={() => setCreating(true)}>
          <PlusIcon />
          <span>Nuevo tipo</span>
        </button>
      </div>

      <div className="AdminCard AdminCard--table">
        <div className="ActList" role="list">
          {types === null ? (
            <div className="AdminTable__state">Cargando tipos de actividad…</div>
          ) : error ? (
            <div className="AdminTable__state AdminTable__state--error">{error}</div>
          ) : types.length === 0 ? (
            <div className="AdminTable__state">Aún no hay tipos de actividad. Crea el primero.</div>
          ) : (
            types.map((at, i) => {
              const customCount = readCustomFields(at).length;
              const defaultCount = readDefaultFields(at).length;
              return (
                <button
                  key={at.id ?? activityTypeName(at) ?? i}
                  type="button"
                  className="ActList__row"
                  role="listitem"
                  onClick={() => setSelectedId(at.id)}
                >
                  <span className="ActList__mark" aria-hidden="true">
                    <LayersIcon />
                  </span>
                  <span className="ActList__text">
                    <strong>{activityTypeName(at)}</strong>
                    {at.slug && <em>{at.slug}</em>}
                  </span>
                  <span className="ActList__meta">
                    {defaultCount > 0 && (
                      <span className="ActChip ActChip--muted">{defaultCount} por defecto</span>
                    )}
                    <span className="ActChip">
                      {customCount} {customCount === 1 ? 'campo' : 'campos'}
                    </span>
                  </span>
                  <span className="ActList__chevron" aria-hidden="true">›</span>
                </button>
              );
            })
          )}
        </div>
      </div>

      {selectedId != null && (
        <ActivityTypeEditor
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onSaved={handleSaved}
        />
      )}
      {creating && (
        <CreateActivityTypeModal onClose={() => setCreating(false)} onSaved={handleSaved} />
      )}
    </section>
  );
}

/**
 * Modal editor for a single activity type's custom fields. Loads the type via
 * GET /{id}, lets the admin add/edit/remove custom fields, and saves them in one
 * call with PUT /{id}/fields. Default fields are shown read-only.
 */
function ActivityTypeEditor({ id, onClose, onSaved }) {
  const [at, setAt] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getAdminActivityType(id);
        if (!alive) return;
        setAt(res);
        setFields(readCustomFields(res));
      } catch {
        if (alive) setLoadError('No hemos podido cargar este tipo de actividad.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const defaults = readDefaultFields(at);

  const patchField = (uid, patch) =>
    setFields((prev) => prev.map((f) => (f.uid === uid ? { ...f, ...patch } : f)));

  const addField = () =>
    setFields((prev) => [
      ...prev,
      {
        uid: nextFieldUid(),
        key: '',
        label: '',
        type: 'STRING',
        placeholder: '',
        required: false,
        editableByAdmin: true,
        options: [],
      },
    ]);

  const removeField = (uid) => setFields((prev) => prev.filter((f) => f.uid !== uid));

  const handleSave = async () => {
    // Fill in a key from the label where the admin left it blank, then validate.
    const cleaned = fields.map((f) => {
      const label = f.label.trim();
      const key = (f.key.trim() || slugifyKey(label));
      return { ...f, label, key };
    });
    if (cleaned.some((f) => !f.label)) {
      setSaveError('Cada campo necesita un nombre.');
      return;
    }
    const badKey = cleaned.find((f) => !KEY_PATTERN.test(f.key));
    if (badKey) {
      setSaveError(
        `La clave "${badKey.key || badKey.label}" no es válida: usa minúscula inicial y solo letras, números o guion bajo.`
      );
      return;
    }
    const keys = cleaned.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      setSaveError('Hay claves de campo repetidas.');
      return;
    }
    // Choice fields need at least one option.
    const isChoice = (f) => f.type === 'ENUM';
    const cleanOptions = (f) =>
      (f.options || []).map((o) => o.trim()).filter(Boolean);
    const missingOptions = cleaned.find(
      (f) => isChoice(f) && cleanOptions(f).length === 0
    );
    if (missingOptions) {
      setSaveError(
        `El campo "${missingOptions.label}" es una lista: añade al menos una opción.`
      );
      return;
    }
    setSaving(true);
    setSaveError('');
    const payload = cleaned.map((f) => {
      const base = {
        key: f.key,
        label: f.label,
        type: f.type,
        placeholder: f.placeholder.trim() || null,
        required: f.required,
      };
      if (isChoice(f)) base.options = cleanOptions(f);
      return base;
    });
    try {
      await replaceAdminActivityTypeFields(id, payload);
      onSaved();
    } catch (err) {
      setSaveError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido guardar los cambios.'
      );
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={at ? activityTypeName(at) : 'Cargando…'}
      subtitle="Campos personalizados"
      onClose={onClose}
      busy={saving}
      wide
    >
      {loadError ? (
        <p className="Modal__error">{loadError}</p>
      ) : at === null ? (
        <p className="ActEditor__loading">Cargando campos…</p>
      ) : (
        <>
          {defaults.length > 0 && (
            <div className="ActEditor__block">
              <span className="ActEditor__blockTitle">Campos por defecto</span>
              <p className="ActEditor__blockHint">
                Se conservan siempre y no pueden editarse.
              </p>
              <div className="ActEditor__defaults">
                {defaults.map((f) => (
                  <span key={f.uid} className="ActDefault">
                    {f.label}
                    <em>{FIELD_TYPE_OPTIONS.find((o) => o.value === f.type)?.label || f.type}</em>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="ActEditor__block">
            <span className="ActEditor__blockTitle">Campos personalizados</span>
            {fields.length === 0 ? (
              <p className="ActEditor__empty">
                Sin campos personalizados todavía. Añade el primero.
              </p>
            ) : (
              <div className="ActEditor__fields">
                {fields.map((f) => (
                  <FieldRow
                    key={f.uid}
                    field={f}
                    onChange={(patch) => patchField(f.uid, patch)}
                    onRemove={() => removeField(f.uid)}
                  />
                ))}
              </div>
            )}
            <button type="button" className="ActEditor__add" onClick={addField}>
              <PlusIcon />
              <span>Añadir campo</span>
            </button>
          </div>

          {saveError && <p className="Modal__error">{saveError}</p>}

          <div className="Modal__actions">
            <button type="button" className="Btn Btn--ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="button" className="Btn Btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

/** One editable custom-field row inside the editor. */
function FieldRow({ field, onChange, onRemove }) {
  // The `key` auto-follows the label until the admin edits it by hand.
  const [keyTouched, setKeyTouched] = useState(Boolean(field.key));

  const onLabel = (label) => {
    onChange(keyTouched ? { label } : { label, key: slugifyKey(label) });
  };

  return (
    <div className="FieldRow">
      <div className="FieldRow__main">
        <label className="FieldRow__cell FieldRow__cell--label">
          <span className="FieldRow__tag">Nombre</span>
          <input
            className="FieldRow__input"
            type="text"
            value={field.label}
            placeholder="p. ej. Dificultad"
            onChange={(e) => onLabel(e.target.value)}
          />
        </label>
        <div className="FieldRow__cell FieldRow__cell--type">
          <span className="FieldRow__tag">Tipo</span>
          <FlikSelect
            variant="line"
            value={field.type}
            onChange={(value) => onChange({ type: value })}
            options={FIELD_TYPE_OPTIONS}
            ariaLabel="Tipo de campo"
          />
        </div>
        <label className="FieldRow__req">
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          <span>Obligatorio</span>
        </label>
        <button
          type="button"
          className="FieldRow__del"
          onClick={onRemove}
          aria-label="Eliminar campo"
        >
          <TrashIcon />
        </button>
      </div>
      <div className="FieldRow__extra">
        <label className="FieldRow__cell FieldRow__cell--key">
          <span className="FieldRow__tag">Clave</span>
          <input
            className="FieldRow__input FieldRow__input--mono"
            type="text"
            value={field.key}
            placeholder="dificultad"
            onChange={(e) => {
              setKeyTouched(true);
              onChange({ key: e.target.value });
            }}
          />
        </label>
        <label className="FieldRow__cell FieldRow__cell--ph">
          <span className="FieldRow__tag">Marcador (opcional)</span>
          <input
            className="FieldRow__input"
            type="text"
            value={field.placeholder}
            placeholder="Texto de ayuda"
            onChange={(e) => onChange({ placeholder: e.target.value })}
          />
        </label>
      </div>
      {field.type === 'ENUM' && (
        <div className="FieldRow__extra">
          <label className="FieldRow__cell FieldRow__cell--options">
            <span className="FieldRow__tag">Opciones (una por línea)</span>
            <textarea
              className="FieldRow__input FieldRow__textarea"
              rows={Math.max(3, (field.options || []).length + 1)}
              value={(field.options || []).join('\n')}
              placeholder={'Fácil\nMedia\nDifícil'}
              onChange={(e) =>
                onChange({ options: e.target.value.split('\n') })
              }
              onBlur={(e) =>
                onChange({
                  options: e.target.value
                    .split('\n')
                    .map((o) => o.trim())
                    .filter(Boolean),
                })
              }
            />
          </label>
        </div>
      )}
    </div>
  );
}

/** Modal to create a new activity type (name + optional description). */
function CreateActivityTypeModal({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Ponle un nombre al tipo de actividad.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createAdminActivityType({ name: name.trim() });
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido crear el tipo de actividad.'
      );
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Nuevo tipo de actividad" subtitle="Los campos por defecto se añaden solos" onClose={onClose} busy={saving}>
      <label className="FieldRow__cell">
        <span className="FieldRow__tag">Nombre</span>
        <input
          className="FieldRow__input"
          type="text"
          value={name}
          autoFocus
          placeholder="p. ej. Senderismo"
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <p className="ActEditor__blockHint" style={{ marginTop: 12 }}>
        Podrás añadir campos personalizados tras crearlo.
      </p>

      {error && <p className="Modal__error">{error}</p>}

      <div className="Modal__actions">
        <button type="button" className="Btn Btn--ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button type="button" className="Btn Btn--primary" onClick={handleCreate} disabled={saving}>
          {saving ? 'Creando…' : 'Crear tipo'}
        </button>
      </div>
    </ModalShell>
  );
}

/* -------------------------------- routes ------------------------------- */

/** Best-effort display name for an activity's type slug. */
function routeTypeLabel(slug, typesBySlug) {
  const t = typesBySlug?.[slug];
  if (t) return t.name || t.label || slug;
  return resolveActivityType(slug).label;
}

/**
 * "Rutas" section: manages the activities/routes shown on the map. Lists every
 * activity (GET /activities), filterable by name and type, with a "Nueva ruta"
 * button and per-row edit (PUT /activities/{id}) and delete (DELETE
 * /activities/{id}) actions. Mirrors the Tipos de actividad layout.
 */
function RoutesSection() {
  const [activities, setActivities] = useState(null); // array or null while loading
  const [types, setTypes] = useState([]); // catalogue for the editor + labels
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editing, setEditing] = useState(null); // activity, or { __new: true }
  const [deleting, setDeleting] = useState(null); // activity pending confirmation

  const load = React.useCallback(async () => {
    setError('');
    try {
      const [list, cat] = await Promise.all([
        searchActivities(),
        getActivityTypes().catch(() => []),
      ]);
      const rows = Array.isArray(list) ? list : list?.content || list?.items || [];
      const catArr = Array.isArray(cat) ? cat : cat?.content || [];
      setActivities(rows);
      setTypes(catArr);
    } catch {
      setError('No hemos podido cargar las rutas.');
      setActivities([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const typesBySlug = useMemo(() => {
    const map = {};
    types.forEach((t) => {
      if (t?.slug) map[t.slug] = t;
    });
    return map;
  }, [types]);

  const typeOptions = useMemo(
    () => [
      { value: '', label: 'Todos los tipos' },
      ...types.map((t) => ({ value: t.slug, label: t.name || t.slug })),
    ],
    [types]
  );

  const filtered = useMemo(() => {
    if (!activities) return [];
    const q = searchInput.trim().toLowerCase();
    return activities.filter((a) => {
      if (typeFilter && a.activityType !== typeFilter) return false;
      if (q && !String(a.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [activities, searchInput, typeFilter]);

  const handleSaved = () => {
    setEditing(null);
    setActivities(null);
    load();
  };

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    await deleteActivity(deleting.id);
    setDeleting(null);
    setActivities((prev) => (prev ? prev.filter((a) => a.id !== deleting.id) : prev));
  };

  return (
    <section className="Admin__section" aria-label="Rutas">
      <div className="Admin__sectionHead">
        <div>
          <h1 className="Admin__title">Rutas</h1>
          <p className="Admin__subtitle">
            {activities
              ? `${activities.length} ${activities.length === 1 ? 'ruta publicada' : 'rutas publicadas'}`
              : 'Gestiona las rutas y actividades del mapa.'}
          </p>
        </div>
        <button type="button" className="Admin__cta" onClick={() => setEditing({ __new: true })}>
          <PlusIcon />
          <span>Nueva ruta</span>
        </button>
      </div>

      <div className="Admin__toolbar">
        <div className="Admin__searchBox">
          <SearchIcon />
          <input
            type="search"
            className="Admin__search"
            placeholder="Buscar por nombre…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Buscar rutas"
          />
        </div>
        <FlikSelect
          variant="box"
          value={typeFilter}
          onChange={setTypeFilter}
          options={typeOptions}
          ariaLabel="Filtrar por tipo"
        />
      </div>

      <div className="AdminCard AdminCard--table">
        <div className="ActList" role="list">
          {activities === null ? (
            <div className="AdminTable__state">Cargando rutas…</div>
          ) : error ? (
            <div className="AdminTable__state AdminTable__state--error">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="AdminTable__state">
              {activities.length === 0
                ? 'Aún no hay rutas. Crea la primera.'
                : 'No hay rutas que coincidan con el filtro.'}
            </div>
          ) : (
            filtered.map((a) => (
              <div className="ActList__row ActList__row--static" role="listitem" key={a.id}>
                <span className="ActList__mark" aria-hidden="true">
                  <ActivityTypeIcon activityType={a.activityType} size={20} />
                </span>
                <span className="ActList__text">
                  <strong>{a.name || `Ruta #${a.id}`}</strong>
                  <em>{routeTypeLabel(a.activityType, typesBySlug)}</em>
                </span>
                <span className="ActList__actions">
                  <button
                    type="button"
                    className="RouteAction"
                    onClick={() => setEditing(a)}
                    aria-label={`Editar ${a.name || 'ruta'}`}
                  >
                    <PencilIcon />
                  </button>
                  <button
                    type="button"
                    className="RouteAction RouteAction--danger"
                    onClick={() => setDeleting(a)}
                    aria-label={`Eliminar ${a.name || 'ruta'}`}
                  >
                    <TrashIcon />
                  </button>
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {editing && (
        <ActivityEditorModal
          activity={editing.__new ? null : editing}
          types={types}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
      {deleting && (
        <ConfirmDeleteModal
          name={deleting.name || `Ruta #${deleting.id}`}
          onCancel={() => setDeleting(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </section>
  );
}

/**
 * Create/edit modal for a route. Builds the field form dynamically from the
 * chosen activity type (like the map's "add activity" dialog), styled to match
 * the admin dashboard. On save it POSTs (create) or PUTs (edit) the activity.
 */
function ActivityEditorModal({ activity, types, onClose, onSaved }) {
  const isEdit = Boolean(activity);
  const [typeSlug, setTypeSlug] = useState(activity?.activityType || '');
  const [name, setName] = useState(activity?.name || '');
  const [values, setValues] = useState(() => ({ ...(activity?.values || {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedType = useMemo(
    () => (types || []).find((t) => t.slug === typeSlug) || null,
    [types, typeSlug]
  );

  // On create, reset the dynamic values whenever the chosen type changes.
  useEffect(() => {
    if (isEdit) return;
    setValues({});
    setError('');
  }, [typeSlug, isEdit]);

  const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const coerce = (field, raw) => {
    if (field.type === 'BOOLEAN') return Boolean(raw);
    if (raw === '' || raw === undefined || raw === null) return undefined;
    if (field.type === 'DOUBLE' || field.type === 'INTEGER') {
      const n = Number(raw);
      return Number.isNaN(n) ? undefined : n;
    }
    return raw;
  };

  const handleSave = async () => {
    if (!selectedType) {
      setError('Escoge un tipo de actividad.');
      return;
    }
    if (!name.trim()) {
      setError('Ponle un nombre a la ruta.');
      return;
    }
    const payloadValues = {};
    for (const field of selectedType.fields || []) {
      const coerced = coerce(field, values[field.key]);
      if (coerced === undefined) {
        if (field.required) {
          setError(`El campo "${field.label}" es obligatorio.`);
          return;
        }
        continue;
      }
      payloadValues[field.key] = coerced;
    }

    setSaving(true);
    setError('');
    const body = { name: name.trim(), activityType: selectedType.slug, values: payloadValues };
    try {
      if (isEdit) {
        await updateActivity(activity.id, body);
      } else {
        await createActivity(body);
      }
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido guardar la ruta.'
      );
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? name || 'Editar ruta' : 'Nueva ruta'}
      subtitle={isEdit ? 'Editar ruta' : 'Crear ruta'}
      onClose={onClose}
      busy={saving}
      wide
    >
      {types.length === 0 ? (
        <p className="Modal__error">
          No hay tipos de actividad. Crea uno en “Tipos de actividad” antes de añadir rutas.
        </p>
      ) : (
        <>
          <label className="FieldRow__cell">
            <span className="FieldRow__tag">Tipo de actividad</span>
            <FlikSelect
              variant="line"
              value={typeSlug}
              onChange={setTypeSlug}
              disabled={isEdit}
              placeholder="Selecciona un tipo…"
              ariaLabel="Tipo de actividad"
              options={(types || []).map((t) => ({
                value: t.slug,
                label: t.name || t.slug,
                icon: <ActivityTypeIcon activityType={t.slug} size={18} />,
              }))}
            />
          </label>

          {selectedType && (
            <>
              <label className="FieldRow__cell" style={{ marginTop: 14 }}>
                <span className="FieldRow__tag">Nombre</span>
                <input
                  className="FieldRow__input"
                  type="text"
                  value={name}
                  autoFocus
                  placeholder="Nombre de la ruta"
                  onChange={(e) => setName(e.target.value)}
                />
              </label>

              <div className="ActEditor__fields" style={{ marginTop: 14 }}>
                {(selectedType.fields || []).map((field) => (
                  <RouteField
                    key={field.key}
                    field={field}
                    value={values[field.key]}
                    onChange={(v) => setValue(field.key, v)}
                  />
                ))}
              </div>
            </>
          )}

          {error && <p className="Modal__error">{error}</p>}

          <div className="Modal__actions">
            <button type="button" className="Btn Btn--ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button
              type="button"
              className="Btn Btn--primary"
              onClick={handleSave}
              disabled={saving || !selectedType}
            >
              {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear ruta'}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

/** One dynamic route field, styled with the admin modal's field inputs. */
function RouteField({ field, value, onChange }) {
  const label = `${field.label}${field.required ? ' *' : ''}`;

  if (field.type === 'BOOLEAN') {
    return (
      <label className="FieldRow__req FieldRow__req--block">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{label}</span>
      </label>
    );
  }

  if (field.type === 'ENUM') {
    const options = (field.options || field.choices || [])
      .map((o) =>
        o && typeof o === 'object'
          ? { value: String(o.value ?? o.key ?? o.id ?? o.name), label: String(o.label ?? o.name ?? o.value) }
          : { value: String(o), label: String(o) }
      )
      .filter((o) => o.value && o.value !== 'undefined');
    return (
      <label className="FieldRow__cell">
        <span className="FieldRow__tag">{label}</span>
        <FlikSelect
          variant="line"
          value={value ?? ''}
          onChange={onChange}
          options={options}
          placeholder={field.placeholder || 'Selecciona una opción'}
          ariaLabel={field.label}
        />
      </label>
    );
  }

  const type = field.type === 'DATE' ? 'date' : field.type === 'DOUBLE' || field.type === 'INTEGER' ? 'number' : 'text';
  return (
    <label className="FieldRow__cell">
      <span className="FieldRow__tag">{label}</span>
      <input
        className="FieldRow__input"
        type={type}
        step={field.type === 'INTEGER' ? '1' : field.type === 'DOUBLE' ? 'any' : undefined}
        value={value ?? ''}
        placeholder={field.placeholder || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** Small confirmation modal for deleting a route. */
function ConfirmDeleteModal({ name, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      await onConfirm();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.detail || err.message : 'No hemos podido eliminar la ruta.'
      );
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Eliminar ruta" subtitle="Acción irreversible" onClose={onCancel} busy={busy}>
      <p className="ActEditor__blockHint">
        ¿Seguro que quieres eliminar <strong>{name}</strong>? Esta acción no se puede deshacer.
      </p>
      {error && <p className="Modal__error">{error}</p>}
      <div className="Modal__actions">
        <button type="button" className="Btn Btn--ghost" onClick={onCancel} disabled={busy}>
          Cancelar
        </button>
        <button type="button" className="Btn Btn--danger" onClick={confirm} disabled={busy}>
          {busy ? 'Eliminando…' : 'Eliminar'}
        </button>
      </div>
    </ModalShell>
  );
}

/** Shared dark-surface modal shell for the admin dashboard. */
function ModalShell({ title, subtitle, onClose, busy, wide, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  // Freeze the page behind the modal while it is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div className="AdminModal" role="presentation" onMouseDown={() => !busy && onClose()}>
      <div
        className={`AdminModal__card${wide ? ' AdminModal__card--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="AdminModal__head">
          <div>
            {subtitle && <span className="AdminModal__eyebrow">{subtitle}</span>}
            <h3 className="AdminModal__title">{title}</h3>
          </div>
          <button
            type="button"
            className="AdminModal__close"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={busy}
          >
            ×
          </button>
        </div>
        <div className="AdminModal__body">{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------- icons --------------------------------- */

function LayersIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M12 3 3 8l9 5 9-5-9-5Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="m3 13 9 5 9-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="M4 7h16M9 7V5h6v2m-8 0 1 13h8l1-13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="4" y="4" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="9" cy="8" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M16 5.2A3.4 3.4 0 0 1 16 11m2 9c0-2.6-1.3-4.5-3.3-5.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function DotIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function RouteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="6" cy="18" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="18" cy="6" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8.4 18H14a3.5 3.5 0 0 0 0-7H9a3.5 3.5 0 0 1 0-7h6.6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="M4 20h4L18.5 9.5a2 2 0 0 0-2.8-2.8L4 18v2Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M14 6.5 17.5 10" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16 16 4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default AdminPage;
